import {
  shops,
  storefrontApiVersion,
  adminApiVersion,
} from "../config/shopify";

/**
 * Configuration for a single shop.
 */
export interface Shop {
  /**
   * Unique handle for shop.
   */
  handle: "staging" | "production";
  /**
   * Numerical ID for the Shopify shop.
   * This can be found in the "Customer Account API" settings on the Headless channel.
   * For example, if the URL is https://shopify.com/authentication/32268419208/oauth/authorize
   * then the Shop ID is 32268419208.
   */
  shopId: number;
  /**
   * The "myshopify.com" domain for the Shopify shop.
   * @example bared-footwear-au.myshopify.com
   */
  shopifyDomain: string;
  /**
   * Domain where the checkout is accessed.
   * @example checkout.baredfootwear.com
   */
  checkoutDomain: string;
  /**
   * Numerical ID for the storefront obtained from the "Headless" channel.
   * This is available from the URL when you click on the storefront in the channel app.
   */
  storefrontId: string;
  /**
   * Public storefront token obtained from the "Headless" channel.
   */
  publicStorefrontToken: string;
  /**
   * Private storefront token obtained from the "Headless" channel.
   */
  privateStorefrontToken?: () => Promise<string | null>;
  /**
   * Admin access token using a private app.
   * Currently, this is used for adding customer tags and metafields.
   */
  adminAccessToken?: () => Promise<string | null>;
  /**
   * Client ID for the "Customer Account API".
   * This can be found in the "Headless" channel settings.
   */
  customerAccountClientId: string;
  /**
   * Client secret for the "Customer Account API".
   * This can be found in the "Headless" channel settings.
   */
  customerAccountClientSecret: () => Promise<string | null>;
}

/**
 * Shop suitable for public.
 */
export type PublicShop = Omit<
  Shop,
  "privateStorefrontToken" | "adminAccessToken"
>;

/**
 * Resolve shop for current request.
 */
export function resolveShop() {
  const shop =
    "bared-footwear-au-stage" === process.env["GATSBY_SHOPIFY_DEFAULT_SHOP"]
      ? shops.find((shop) => "staging" === shop.handle)
      : shops.find((shop) => "production" === shop.handle);

  if (!shop) throw new Error("Could not resolve shop.");

  return shop;
}

/**
 * Return storefront API version.
 */
export function resolveStorefrontApiVersion() {
  return storefrontApiVersion;
}

/**
 * Return admin API version.
 */
export function resolveAdminApiVersion() {
  return adminApiVersion;
}

/**
 * Make the given shop suitable for public exposure.
 */
export function mapPublicShop(shop: Shop): PublicShop {
  const { privateStorefrontToken, adminAccessToken, ...publicShop } = shop;
  return publicShop;
}
